import { useEffect, forwardRef, useImperativeHandle, Ref } from 'react';
import {
	useForm,
	Controller,
	FieldValues,
	DefaultValues,
	SubmitHandler,
} from 'react-hook-form';
import cx from 'classnames';

import { FormProps } from './TextFieldsForm.types';
import styles from './TextFieldsForm.module.scss';
import Input from '../input/Input';
import { IFormRef } from '../../../types';

/**
 * A form component that renders a list of text fields.
 * Exposes the `handleSubmit` function to Parent component using ref from
 * `react-hook-form`'s `useForm` hook.
 *
 * @template TFieldValues - The type of the form field values.
 *
 * @param {FormProps<TFieldValues>} props - The props for the form component.
 * @param {Array} props.fields - An array of field configurations for the form.
 * @param {function} props.onSubmit - The function to call when the form is
 * submitted.
 * @param {React.Ref<IFormRef>} ref - The ref used to expose imperative methods
 * to parent.
 * @param {string} props.formId - The formId used to map the submit button of
 * Parent Component with this form's id to submit the form.
 *
 * @example
 * ```tsx
 * const fields = [
 *   { name: 'firstName', label: 'First Name', type: 'text', defaultValue: '', rules: { required: 'First name is required' } },
 *   { name: 'lastName', label: 'Last Name', type: 'text', defaultValue: '', rules: { required: 'Last name is required' } },
 * ];
 * const formRef = useRef<IFormRef>(null);
 *
 * const handleSubmit = (data) => {
 *   console.log(data);
 * };
 *
 * <TextFieldsForm fields={fields} buttonTitle="Submit" onSubmit={handleSubmit} />
 * ```
 */
const TextFieldsForm = forwardRef<IFormRef, FormProps>(
	<TFieldValues extends FieldValues = FieldValues>(
		{ fields, formId, onSubmit }: FormProps<TFieldValues>,
		ref: Ref<IFormRef>,
	) => {
		const defaultValues = fields.reduce((previousFields, currentField) => {
			previousFields[currentField.name] = currentField.defaultValue || '';
			return previousFields;
		}, {} as DefaultValues<TFieldValues>);

		const { control, handleSubmit, reset } = useForm<TFieldValues>({
			defaultValues,
		});

		useImperativeHandle(ref, () => ({
			validateForm: handleSubmit(onSubmit as SubmitHandler<FieldValues>),
		}));

		// Resetting form on changing fields defaultValue
		useEffect(() => {
			reset(defaultValues);
		}, [fields]);

		const onChangeInput = (
			e: React.ChangeEvent<HTMLInputElement>,
			onChangeFormField: (e: React.ChangeEvent<HTMLInputElement>) => void,
			customOnchange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
		) => {
			onChangeFormField(e);
			customOnchange?.(e);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleSubmit(onSubmit)();
			}
		};

		return (
			<form onKeyDown={handleKeyDown} id={formId}>
				<div className={styles.form}>
					{fields.map((field, index) => {
						return (
							<div
								key={field.name}
								className={cx(styles.inputContainer, {
									[styles.withHalfWidth]: field.withHalfWidth,
									[styles.fullWidth]:
										!field.withHalfWidth ||
										(index === fields.length - 1 && !field.withHalfWidth),
								})}
							>
								<Controller
									name={field.name as TFieldValues[keyof TFieldValues]}
									control={control}
									rules={field.rules}
									render={({
										field: { value, name, onChange },
										fieldState: { error },
									}) => (
										<Input
											placeholder={field.placeholder}
											label={field.label}
											type={field.type}
											helperText={String(error?.message || '')}
											errorText={error?.message}
											status={error?.message ? 'error' : ''}
											value={value}
											size="large"
											name={name}
											onChange={(e: any) =>
												onChangeInput(e, onChange, field.onChange)
											}
											className={field.className}
											readOnly={field.readonly}
										/>
									)}
								/>
							</div>
						);
					})}
				</div>
			</form>
		);
	},
);

export default TextFieldsForm;
